// Signing in, as both designs draw it (Login.tsx in Riverstone, SignInPage.tsx in Blueprint): what the
// company allows, the first factor (a password, or a one-time code by email or text) and the second step
// owed after it (decision 20, QA-093). The answers never say whether an email or mobile is known.
import { useEffect, useState, type FormEvent } from 'react';
import { RpcError, type SignInMethod, type SignInResult, type TwoFactorMethod } from '@flowology/client';
import { client } from './api.ts';
import { TWO_FACTOR_LABEL, codeLooksRight, firstMethod } from './account/signin-model.ts';

// Off a company's own hostname (local development) the company is named in the form. Read only in a
// browser: the catalogue's script loads the stories in Node.
export const onCompanyHost =
  typeof window !== 'undefined' && !/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);

const ALL: readonly SignInMethod[] = ['password', 'google', 'microsoft', 'email_code', 'sms_code'];
const PROVIDER_ERRORS: Record<string, string> = {
  google_failed: 'Google couldn’t sign you in. Try again, or use another way below.',
  microsoft_failed: 'Microsoft couldn’t sign you in. Try again, or use another way below.',
  // Unlinked by its person (QA-200): it comes back only by linking it again from Your sign-in.
  google_unlinked:
    'This Google account isn’t linked to Flowology. Sign in another way, then link it in Your sign-in.',
  microsoft_unlinked:
    'This Microsoft account isn’t linked to Flowology. Sign in another way, then link it in Your sign-in.',
};

export type Mode = { kind: 'password' } | { kind: 'code'; channel: 'email' | 'sms'; sent: string | null };
export type SignedIn = (csrf: string, tenant: string, recoveryCodesLeft?: number) => void;

const failed = (err: unknown) =>
  err instanceof RpcError ? err.message : 'Could not reach Flowology. Check your connection.';

/** The first factor, and the second step it may leave owed. */
export function useSignIn({
  onSignedIn,
  tenant: knownTenant,
  again,
  companyHost = onCompanyHost,
}: {
  onSignedIn: SignedIn;
  tenant?: string | undefined;
  again: boolean;
  /** On a company's own host the host says whose sign-in it is; off one the form asks. */
  companyHost?: boolean;
}) {
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [tenant, setTenant] = useState(knownTenant ?? '');
  const [methods, setMethods] = useState<readonly SignInMethod[]>(ALL);
  const [mode, setMode] = useState<Mode>({ kind: 'password' });
  const [error, setError] = useState<string | null>(() => {
    const why = new URLSearchParams(window.location.search).get('signin');
    if (!why) return null;
    window.history.replaceState(null, '', window.location.pathname); // a reload starts clean
    return PROVIDER_ERRORS[why] ?? 'That sign-in didn’t work. Try again, or use another way below.';
  });
  const [busy, setBusy] = useState(false);
  // The second factor still owed after the first (decision 20, QA-093).
  const [second, setSecond] = useState<readonly TwoFactorMethod[] | null>(null);
  // Whose sign-in the second step is for: the first factor said (rs-portal's QA-093 review).
  const [stepCompany, setStepCompany] = useState<string | null>(null);
  const company = companyHost ? undefined : tenant.trim() || undefined;
  const owed = (methods: readonly TwoFactorMethod[], name?: string) => {
    setStepCompany(name || null);
    setSecond(methods);
  };
  const done = (r: SignInResult) =>
    r.twoFactor ? owed(r.twoFactor.methods, r.twoFactor.company) : onSignedIn(r.csrf, r.tenant);

  // Back from Google, Microsoft or a one-tap link with a second factor owed: ask for it here.
  useEffect(() => {
    if (again || (!companyHost && !company)) return;
    client
      .twoFactorPending(company)
      .then((p) => p.pending && p.methods && owed(p.methods, p.company))
      .catch(() => undefined);
  }, [company, again]);

  // What this company allows; until it answers (or off a company host before one is named), all.
  useEffect(() => {
    if (!companyHost && !company) return setMethods(ALL);
    const t = setTimeout(() => {
      client
        .signInMethods(company)
        .then((r) => {
          setMethods(r.methods.length ? r.methods : ALL);
          if (!r.methods.includes('password') && mode.kind === 'password')
            setMode({
              kind: 'code',
              channel: r.methods.includes('sms_code') && !r.methods.includes('email_code') ? 'sms' : 'email',
              sent: null,
            });
        })
        .catch(() => setMethods(ALL));
    }, 300);
    return () => clearTimeout(t);
  }, [company]); // re-asked only when the company changes

  const fail = (err: unknown) => setError(failed(err));

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      done(await client.signIn(email, password, company));
    } catch (err) {
      setPassword('');
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    if (mode.kind !== 'code') return;
    setBusy(true);
    setError(null);
    const to = mode.channel === 'email' ? { email: email.trim() } : { mobile: mobile.trim() };
    try {
      if (!mode.sent) {
        const r = await client.startCode({ ...to, ...(company ? { tenant: company } : {}) });
        setMode({ ...mode, sent: r.message });
      } else {
        done(
          await client.verifyCode({
            ...to,
            code: code.trim(),
            ...(company ? { tenant: company } : {}),
          }),
        );
      }
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  /** Another way in: the refusal goes with the way it was for. */
  const switchTo = (next: Mode) => {
    setError(null);
    setMode(next);
  };
  /** Back from the second step to the first. */
  const startAgain = () => {
    setSecond(null);
    setStepCompany(null);
    setPassword('');
    setCode('');
  };

  return {
    email,
    setEmail,
    mobile,
    setMobile,
    password,
    setPassword,
    code,
    setCode,
    tenant,
    setTenant,
    mode,
    setMode,
    switchTo,
    error,
    busy,
    second,
    stepCompany,
    company,
    startAgain,
    submitPassword,
    submitCode,
    /** The company field is asked for only off a company host, and not when the session ended. */
    asksCompany: !companyHost && !again,
    google: methods.includes('google'),
    microsoft: methods.includes('microsoft'),
    byEmail: methods.includes('email_code'),
    byText: methods.includes('sms_code'),
    byPassword: methods.includes('password'),
  };
}

/** Where Google's or Microsoft's sign-in starts, for this company when the form names one. */
export const providerStart = (provider: 'google' | 'microsoft', company?: string) =>
  `/auth/${provider}/start${company ? `?tenant=${encodeURIComponent(company)}` : ''}`;

/** The second step: an app code, a text code to the verified mobile, or a recovery code. */
export function useSecondStep({
  methods,
  tenant,
  onSignedIn,
}: {
  methods: readonly TwoFactorMethod[];
  tenant?: string | undefined;
  onSignedIn: SignedIn;
}) {
  const [method, setMethod] = useState<TwoFactorMethod>(firstMethod(methods));
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(false);
  const [texted, setTexted] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fail = (e: unknown) => setError(failed(e));

  async function sendText() {
    setBusy(true);
    setError(null);
    try {
      const r = await client.twoFactorText(tenant);
      setTexted(r.phoneTail);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!codeLooksRight(method, code)) return setError(TWO_FACTOR_LABEL[method].hint);
    setBusy(true);
    setError(null);
    try {
      const r = await client.twoFactorVerify({
        method,
        code: code.replace(/\s+/g, ''),
        remember,
        ...(tenant ? { tenant } : {}),
      });
      onSignedIn(r.csrf, r.tenant, r.recoveryCodesLeft);
    } catch (err) {
      setCode('');
      fail(err);
    } finally {
      setBusy(false);
    }
  }

  /** Another way to confirm it's them; asking for a text sends it. */
  const switchTo = (m: TwoFactorMethod) => {
    setMethod(m);
    setCode('');
    setError(null);
    if (m === 'sms' && !texted) void sendText();
  };

  return {
    method,
    label: TWO_FACTOR_LABEL[method],
    others: methods.filter((m) => m !== method),
    code,
    setCode,
    remember,
    setRemember,
    texted,
    error,
    busy,
    sendText,
    submit,
    switchTo,
    /** The lede under the title: the texted mobile's last digits once the text has gone. */
    lede:
      method === 'sms' && texted
        ? `We’ve texted a code to the mobile ending ${texted}. It works for 5 minutes.`
        : TWO_FACTOR_LABEL[method].lede,
  };
}
